import { get, uniqBy } from 'lodash';
import { Observable, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  ALLOW_MBC_LOGIN,
  IS_LOCAL_ENV,
  IS_QA_ENV,
  SHOW_COUNSELED_YOUTH,
} from '@config/config';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { authApi } from '@modules/rtk/auth-api';
import { esbApi } from '@modules/rtk/esb-api';
import { observableFromService } from '@modules/shared/utils/observableFromRtkService';
import { toastService } from '@modules/toasts';
import userPreferencesServices from '@shared/duck/userPreferencesServices';
import userServices from '@shared/duck/userServices';
import esbApiService from '@utils/http/esbApiService';
import personNameBuilder from '@utils/personNameBuilder';

import unitSrv from '../../unit/duck/services';
import { dedupeRoleTypes } from '../helpers';
import {
  addIsScoutbookRole,
  enhanceWithUnitProfile,
  filterValidOrgs,
  normalizeUserRole,
} from '../helpersTyped';
import { handleAuthenticateError } from './utils';

const defaultAppStorageName = 'DEFAULT_APP';

const noop = () => {};

/**
 * @param {import('@reduxjs/toolkit').ThunkDispatch} dispatch
 */
const login$ = (login, password, dispatch) => {
  const thunk = dispatch(
    authApi.endpoints.authenticate.initiate({
      username: login,
      body: { password },
    }),
  )
    .then(result => {
      if (result.error) throw result.error;

      return result.data;
    })
    .catch(error => {
      handleAuthenticateError(error);
      throw error;
    });

  return Observable.fromPromise(thunk).pipe(map(userServices.addSessionExp));
};

/**
 * @param {import('@reduxjs/toolkit').ThunkDispatch} dispatch
 * @param {{headers: Record<string, string>}} extraParams @deprecated
 */
const selfsession$ = (
  _extraParams,
  isExternalLogin = false,
  dispatch,
  token,
  isAppleLogin,
) => {
  const tokenAlt = userServices.retrieveLoginData()?.token;
  if ((IS_QA_ENV || IS_LOCAL_ENV) && !token) {
    // eslint-disable-next-line no-console
    console.debug('Session refresh called without token, using localstorage.', {
      token,
      tokenAlt,
    });
  }
  const usedToken = token || tokenAlt;
  // TODO: Replace apple login once social auth supports
  const service =
    isAppleLogin || !featureFlags.getFlag('SBL_6926_NEW_SESSION_LOGIC')
      ? authApi.endpoints.oldSessionRefresh.initiate({ token: usedToken })
      : authApi.endpoints.sessionRefresh.initiate({ token: usedToken });
  const thunk = dispatch(service)
    .then(result => {
      if (result.error) throw result.error;

      return result.data;
    })
    .catch(error => {
      if (!isExternalLogin && error.status !== 401) {
        toastService.error(error.data?.message || 'Invalid session.');
      }
      throw error;
    });

  return Observable.fromPromise(thunk).pipe(map(userServices.addSessionExp));
};

/**
 * @esbEndpoint GET /persons/v2/:userId/personprofile
 */
const getUserProfileRequest$ = id =>
  esbApiService.get$(`/persons/v2/${id}/personprofile`, {
    gtm: {
      label: '/persons/v2/{userId}/personprofile',
    },
  });

/**
 * @esbEndpoint GET /persons/v2/:personGuid/personprofile
 */
const getCompleteUserProfileRequest$ = (personGuid, { dispatch }) => {
  if (!featureFlags.getFlag('SBL_5499_COMPLETE_PROFILE_API')) {
    return esbApiService.get$(`/persons/v2/${personGuid}/personprofile`, {
      gtm: {
        label: '/persons/v2/{personGuid}/personprofile',
      },
    });
  } else {
    return observableFromService(
      args =>
        dispatch(esbApi.endpoints.getCompletePersonProfile.initiate(args)),
      { personGuid },
    );
  }
};
/**
 * @param {import('@reduxjs/toolkit').ThunkDispatch} dispatch
 */
const masquerade$ = (memberId, dispatch) => {
  const thunk = dispatch(
    authApi.endpoints.masquerade.initiate({
      memberId,
    }),
  )
    .then(result => {
      if (result.error) throw result.error;
      return result.data;
    })
    .catch(error => {
      if (error.data?.code === 'client_41') {
        toastService.error('Masquerade failed.');
      } else {
        toastService.error(error.data?.message);
      }
      throw error;
    });

  return Observable.fromPromise(thunk).pipe(map(userServices.addSessionExp));
};

const getUserProfile$ = (personGuid, userId, { dispatch }) =>
  forkJoin(
    getCompleteUserProfileRequest$(personGuid, { dispatch }),
    userId ? getUserProfileRequest$(userId) : Observable.of({}),
  ).pipe(
    map(([akelaProfile, sbProfile]) => {
      const { organizationPositions = [], ...userProfileData } = akelaProfile;
      const imagePath = get(sbProfile, 'profile.imagePath');
      const {
        organizationPositions: sbOrganizationPositions = [],
        profile: profileFromSB = {},
      } = sbProfile;

      // Proritize getting unitId(sbOrganizationId) from the akela profile instead of sb
      const sbOrganizationIdsFromAkela = organizationPositions
        .filter(pos => pos.sbOrganizationId)
        .map(pos => ({
          organizationGuid: pos.organizationGuid,
          organizationId: `${pos.sbOrganizationId}`,
        }));
      const newSbOrganizationPositions = featureFlags.getFlag(
        'SBL_6904_ENABLE_AKELA_SBORGID',
      )
        ? uniqBy(
            sbOrganizationIdsFromAkela.concat(sbOrganizationPositions),
            'organizationGuid',
          )
        : sbOrganizationPositions;
      userProfileData.profile = {
        ...userProfileData.profile,
        shortName: personNameBuilder.short(userProfileData.profile),
        fullName: personNameBuilder.full(userProfileData.profile),
        isAdult: profileFromSB.isAdult,
        imagePath,
      };
      userProfileData.sbProfile = sbProfile;
      return {
        ...userProfileData,
        sbOrganizationIds: newSbOrganizationPositions.reduce(
          (acc, { organizationGuid, organizationId }) => {
            acc[organizationGuid] = organizationId;
            return acc;
          },
          {},
        ),
      };
    }),
  );

/**
 * @esbEndpoint GET /persons/:personGuid/roleTypes
 */
const getUserRolesRequest$ = (
  id,
  /** @type {import('@reduxjs/toolkit').ThunkDispatch} */
  dispatch,
) =>
  Observable.fromPromise(
    dispatch(
      esbApi.endpoints.getRoleTypes.initiate({
        personGuid: id,
        params: {
          includeParentRoles: 'true',
          includeScoutbookRoles: 'true',
        },
      }),
    ).then(result => {
      if (result.error) throw result;
      return result.data;
    }),
  );

const getUserRoles$ = (personGuid, userId, { dispatch }) =>
  forkJoin(getUserRolesRequest$(personGuid, dispatch), Observable.of([])).pipe(
    map(([personGuidRoles, userIdRoles]) =>
      addIsScoutbookRole(personGuidRoles, userIdRoles),
    ),
    map(dedupeRoleTypes),
    switchMap(roles =>
      forkJoin(
        Observable.of(roles),
        ...roles
          .filter(role => role.organizationGuid && !role.programTypeId)
          .map(role => unitSrv.getUnitInfo$(role.organizationGuid, dispatch)),
      )
        // eslint-disable-next-line no-console
        .catch(err => console.error('err', err))
        .pipe(
          map(([roles, ...profiles]) =>
            enhanceWithUnitProfile(roles, profiles),
          ),
          map(filterValidOrgs),
          map(roles =>
            roles
              .map(role => normalizeUserRole(role, personGuid))
              .filter(
                ({
                  unitTypeId,
                  isCouncil,
                  isMeritBadgeCounselor,
                  isYouthMember,
                  isParentGuardian,
                  hasLogInRole,
                  organizationGuid,
                }) => {
                  if (!isCouncil && !organizationGuid && !isParentGuardian) {
                    return false;
                  }
                  return (
                    unitTypeId ||
                    isCouncil ||
                    hasLogInRole ||
                    ((SHOW_COUNSELED_YOUTH || ALLOW_MBC_LOGIN) &&
                      isMeritBadgeCounselor) ||
                    isYouthMember ||
                    isParentGuardian
                  );
                },
              ),
          ),
        ),
    ),
  );

/**
 * @esbEndpoint GET /persons/v2/:personGuid/trainings/ypt
 */
const getUserYPTrainingRequest$ = (id, { dispatch }) =>
  Observable.fromPromise(
    dispatch(
      esbApi.endpoints.getPersonTrainingsYPT.initiate({ personGuid: id }),
    ).then(result => {
      if (result.error?.status === 401) return {};

      return result.data;
    }),
  );

/**
 * @esbEndpoint GET /persons/:personGuid/subscriptions
 */

const getUsersubscriptionsRequest$ = (personGuid, { dispatch }) =>
  Observable.fromPromise(
    dispatch(
      esbApi.endpoints.getUserSubscriptions.initiate({ personGuid }),
    ).then(result => {
      if (result.error) throw result;
      return result.data;
    }),
  );

const getUserData$ = (personGuid, userId, { dispatch }) =>
  forkJoin(
    getUserProfile$(personGuid, userId, { dispatch }),
    getUserRoles$(personGuid, userId, { dispatch }),
    getUserYPTrainingRequest$(personGuid, { dispatch }),
    getUsersubscriptionsRequest$(personGuid, { dispatch }),
  ).pipe(
    map(([profile, organizations, ypTraining, subscriptions]) => {
      const { sbOrganizationIds, ...profileData } = profile;
      return {
        ...profileData,
        ypTraining,
        subscriptions,
        // TODO: move to getUserRoles$
        hasCouncilPosition: organizations.some(({ isCouncil }) => isCouncil),
        // TODO: move to getUserRoles$
        hasCouncilEditRights: organizations.some(
          ({ hasCouncilEditRights }) => hasCouncilEditRights,
        ),
        // TODO: move to getUserRoles$ (need to see how to inject orgGuid)
        organizations: organizations.map(org => {
          const organizationId = sbOrganizationIds[org.organizationGuid];
          return {
            ...org,
            // TODO: move this to a centralized function
            unitId: organizationId,
          };
        }),
      };
    }),
  );

const logout = async (
  params = {
    isDiscourseSessionPreserved: true,
    /** @type {ThunkDispatch} */
    dispatch: null,
  },
) => {
  // SBL-5729 disable momentarily
  //const isDiscourseSessionPreserved = !!params?.isDiscourseSessionPreserved;
  //
  //await webscriptApiService
  //  .get(
  //    `/WebScript/Output/proxy.aspx?Method=ExecuteScriptSet&ScriptSetCode=DiscourseSSOAgent&ScriptCode=ProcessSSOLogout&IsDiscourseSessionPreserved=${
  //      isDiscourseSessionPreserved ? 1 : 0
  //    }`,
  //    {
  //      suppressErrorToasts: true,
  //      suppressSentry: true,
  //      gtm: {
  //        label: '/logout-discourse',
  //      },
  //    },
  //  )
  //  .catch(noop);

  params.dispatch(authApi.endpoints.logout.initiate()).then(noop).catch(noop);
};

const storeDefaultApp = (personGuid, app) =>
  userPreferencesServices.storePreferences({
    featureName: defaultAppStorageName,
    personGuid,
    preferences: app,
  });

const retrieveDefaultApp = personGuid =>
  userPreferencesServices.retrievePreferences({
    personGuid,
    featureName: defaultAppStorageName,
  });

export default {
  login$,
  getUserYPTrainingRequest$,
  getCompleteUserProfileRequest$,
  getUsersubscriptionsRequest$,
  selfsession$,
  masquerade$,
  logout,
  getUserData$,
  getUserRoles$,
  storeDefaultApp,
  retrieveDefaultApp,
  ...userServices,
};
